import capitalize from 'lodash/capitalize';

import { singularize } from 'utils';
import {
  collectionFromUrlParam,
  collectionLinkTarget,
  contentLookupRecordLink,
} from 'utils/content';

import {
  EVENT_ICONS,
  EVENT_ICON_DEFAULT,
  EVENT_SENSITIVE_KEY_REGEX,
  EVENT_SETTINGS_LABELS,
  EVENT_SETTINGS_MODEL,
  EVENT_SETTINGS_SECTIONS,
} from 'constants/events';

const OBJECT_ID_REGEX = /^[0-9a-f]{24}$/i;

const MAX_FLATTEN_DEPTH = 6;

/**
 * Event ids, request ids and record ids are all 24-char hex ObjectIDs. The
 * API cannot regex-match them (they are stored as ObjectIDs), so the search
 * box has to route them to an exact-match query instead of `search`.
 *
 * @param {unknown} value
 * @returns {boolean}
 */
export function isObjectId(value) {
  return typeof value === 'string' && OBJECT_ID_REGEX.test(value.trim());
}

/**
 * The verb at the end of an event type, e.g. 'updated' for 'product.variant.updated'.
 *
 * @param {object} event
 * @returns {string}
 */
export function getEventVerb(event) {
  const types = String(event?.type || '').split('.');
  return types[types.length - 1] || '';
}

/**
 * `created` and `deleted` events store the full record rather than a diff
 * (schema-api-server server/model.js captureEvents), so they are summarized
 * instead of rendered as changes.
 *
 * @param {object} event
 * @returns {boolean}
 */
export function isSnapshotEvent(event) {
  const verb = getEventVerb(event);
  return verb === 'created' || verb === 'deleted';
}

/**
 * The human name for a settings section. Unknown ids — app settings are stored
 * under the app's id — fall back to title case so they still read as something.
 *
 * @param {string} id
 * @param {object} [appsById] installed apps, keyed by id
 * @returns {string}
 */
export function getSettingsSectionLabel(id, appsById) {
  if (typeof id !== 'string' || !id) {
    return '';
  }

  // An app's settings are stored under the app's id rather than a section name
  // (schema-api-server Model.ensureRequestSettingConfig), so name the app when
  // it is installed and say nothing more than "App" when it is not
  if (isObjectId(id)) {
    const name = appsById?.[id]?.name;

    return name ? `${name} app` : 'App';
  }

  return EVENT_SETTINGS_LABELS[id] || capitalize(id.replace(/[_-]+/g, ' '));
}

/**
 * @param {object} event
 * @param {object} [appsById] installed apps, keyed by id
 * @returns {string}
 */
export function getEventLabel(event, appsById) {
  const verb = getEventVerb(event);

  // Every settings section writes to the same model, so the section is the
  // only thing that says what actually changed
  if (event?.model === EVENT_SETTINGS_MODEL) {
    const section = getSettingsSectionLabel(event?.data?.id, appsById);

    return section ? `${section} settings ${verb}` : `Settings ${verb}`;
  }

  const model = collectionFromUrlParam(event?.model || '');
  // Child collections are `parent:child`; the child is the subject
  const name = model.split(':').pop().split('/').pop();

  return `${capitalize(singularize(name))} ${verb}`.trim();
}

/**
 * The app a settings event belongs to. App settings are stored under the app's
 * id in place of a section name, which is what distinguishes them.
 *
 * @param {object} event
 * @returns {string | null}
 */
export function getEventAppId(event) {
  return event?.model === EVENT_SETTINGS_MODEL && isObjectId(event?.data?.id)
    ? event.data.id
    : null;
}

/**
 * @param {object} event
 * @returns {string}
 */
export function getEventIcon(event) {
  return EVENT_ICONS[getEventVerb(event)] || EVENT_ICON_DEFAULT;
}

/**
 * @param {string} path
 * @returns {boolean}
 */
export function isSensitivePath(path) {
  return EVENT_SENSITIVE_KEY_REGEX.test(String(path).split('.').pop());
}

function isPlainObject(value) {
  return (
    value !== null && typeof value === 'object' && value.constructor === Object
  );
}

/**
 * Flatten an event's `data` into dot-paths with their new values.
 *
 * Update events store only the changed paths, with arrays null-padded so that
 * unchanged elements are `null` (schema-api-server Model.applyDataDiffs). The
 * padding is not a reliable index — several diffs on one path re-pad the array
 * — so array entries are keyed by their `id` when they have one.
 *
 * @param {object} data
 * @param {object} [options]
 * @param {string} [options.prefix]
 * @param {number} [options.depth]
 * @returns {Array<{ path: string, value: unknown }>}
 */
export function flattenEventData(data, { prefix = '', depth = 0 } = {}) {
  if (!isPlainObject(data) || depth > MAX_FLATTEN_DEPTH) {
    return [];
  }

  const entries = [];

  for (const [key, value] of Object.entries(data)) {
    // The record id is the subject of the event, not a change
    if (!prefix && key === 'id') {
      continue;
    }

    const path = prefix ? `${prefix}.${key}` : key;

    if (Array.isArray(value)) {
      // Walk the stored array rather than compacting it first: unchanged
      // elements are null padding, so an element's index here is its real
      // position in the record. Compacting would rename `tags[2]` to
      // `tags[0]`, naming the wrong element and colliding in
      // `buildEventChanges` with a genuine earlier change to the first.
      value.forEach((item, index) => {
        if (item === null) {
          return;
        }

        if (!isPlainObject(item)) {
          entries.push({ path: `${path}[${index}]`, value: item });
          return;
        }

        // The API stamps `id` on array elements along a changed path so the
        // element can be identified; it names the entry rather than being a
        // change of its own
        const { id: itemId, ...itemData } = item;

        entries.push(
          ...flattenEventData(itemData, {
            prefix: `${path}[${itemId ?? index}]`,
            depth: depth + 1,
          }),
        );
      });

      continue;
    }

    if (isPlainObject(value)) {
      const nested = flattenEventData(value, {
        prefix: path,
        depth: depth + 1,
      });

      if (nested.length > 0) {
        entries.push(...nested);
        continue;
      }

      // An empty object is itself the change (e.g. a cleared sub-document)
      entries.push({ path, value });
      continue;
    }

    entries.push({ path, value });
  }

  return entries;
}

/**
 * Build the change list for every event on the page.
 *
 * Events never store their previous values, so old values are filled in
 * opportunistically from older events for the same record that are already
 * loaded — the same approach the order Activity feed uses. Anything not on the
 * page is simply shown without an old value.
 *
 * @param {object[]} events newest first (the API default sort is `id desc`)
 * @returns {Map<string, Array<{ path: string, value: unknown, old?: unknown, hasOld?: boolean }>>}
 */
export function buildEventChanges(events = []) {
  const flattened = events.map((event) => flattenEventData(event?.data));
  const changesById = new Map();

  events.forEach((event, index) => {
    const changes = flattened[index].map((entry) => ({ ...entry }));
    const recordId = event?.data?.id;

    if (recordId && !isSnapshotEvent(event)) {
      const pending = new Map();

      changes.forEach((change, position) => {
        pending.set(change.path, position);
      });

      for (let i = index + 1; i < events.length && pending.size > 0; i++) {
        const previous = events[i];

        if (
          previous.model !== event.model ||
          String(previous?.data?.id) !== String(recordId)
        ) {
          continue;
        }

        for (const entry of flattened[i]) {
          const position = pending.get(entry.path);

          if (position !== undefined) {
            changes[position].old = entry.value;
            changes[position].hasOld = true;
            pending.delete(entry.path);
          }
        }
      }
    }

    changesById.set(event.id, changes);
  });

  return changesById;
}

/**
 * Active users come back with `email` aliased from their username and no
 * `username` key; users removed from the store carry both.
 *
 * @param {object} user
 * @returns {string}
 */
export function getUserName(user) {
  return user?.name || user?.email || user?.username || user?.id || '';
}

/**
 * Split an event's changes into the ones shown on the row and the ones kept
 * behind the "more fields" toggle. Nesting makes no difference — the tree
 * rebuilds any parent a shown field needs — so this is purely a cap on how
 * many field values a row shows at once.
 *
 * @param {Array<object>} changes
 * @param {number} limit
 * @returns {{ primary: object[], extra: object[] }}
 */
export function partitionEventChanges(changes = [], limit) {
  return { primary: changes.slice(0, limit), extra: changes.slice(limit) };
}

/**
 * Group flat dot-paths into a tree so nested fields can be shown indented under
 * their parent instead of repeating the whole path on every line.
 *
 * Path segments split on '.', keeping any `[id]` attached to its key, so
 * `items[i1].options[o1].name` nests as items[i1] > options[o1] > name.
 *
 * @param {Array<object>} changes
 * @returns {Array<{ key: string, change?: object, children: Array }>}
 */
export function buildChangeTree(changes = []) {
  const roots = [];
  const byPath = new Map();

  for (const change of changes) {
    const segments = change.path.split('.');
    let parentChildren = roots;
    let prefix = '';

    segments.forEach((segment, index) => {
      prefix = prefix ? `${prefix}.${segment}` : segment;

      let node = byPath.get(prefix);

      if (!node) {
        node = { key: segment, children: [] };
        byPath.set(prefix, node);
        parentChildren.push(node);
      }

      if (index === segments.length - 1) {
        node.change = change;
      }

      parentChildren = node.children;
    });
  }

  return roots;
}

/**
 * Link an event to the record it changed.
 *
 * `contentLookupRecordLink` is the map the rest of the admin uses; it routes
 * shipments/returns/payments to their order and variants to their product, and
 * returns null when the template needs a field the event does not carry (an
 * update diff has no parent_id). `collectionLinkTarget` alone is not enough —
 * it produces paths with no matching route for several standard models.
 *
 * @param {object} event
 * @returns {string | null}
 */
export function getEventRecordLink(event) {
  const recordId = event?.data?.id;

  if (!recordId) {
    return null;
  }

  if (event.model === EVENT_SETTINGS_MODEL) {
    const section = EVENT_SETTINGS_SECTIONS[recordId];
    return section ? `/settings/${section}` : null;
  }

  const collection = collectionFromUrlParam(event.model);

  const viewLink = contentLookupRecordLink(collection, event.data);

  if (viewLink) {
    return viewLink;
  }

  // Custom, child and app models fall through to the generic collection route
  const target = collectionLinkTarget({ model: collection });

  return target ? `${target}/${recordId}` : null;
}

/**
 * The `model` string an event is recorded under (schema-api-server
 * Model.makeModelPathName): app models keep their `apps/<id>/<name>` path,
 * while a namespace is joined to the name with an underscore.
 *
 * @param {{ name: string, namespace?: string, app_id?: string }} model
 * @returns {string}
 */
export function getEventModelPath({ name, namespace, app_id: appId }) {
  if (appId) {
    return `apps/${appId}/${name}`;
  }

  return namespace ? `${namespace}_${name}` : name;
}

/**
 * A model the merchant defined, as opposed to a standard Swell model or one an
 * app installed. Mirrors CUSTOM_QUERY in constants/content.
 *
 * @param {object} model
 * @returns {boolean}
 */
export function isCustomModel(model) {
  if (!model?.client_id || model.app_id) {
    return false;
  }

  const base = model.extends;

  return !base || base === 'com.base' || !base.startsWith('com.');
}

/**
 * The child collection's own name. A normalized child config carries
 * `parent:key` as its `name` (schema-api-server model.js childConfig.name), so
 * taking it whole would double the parent path.
 *
 * @param {object} field
 * @param {string} fieldName
 * @returns {string}
 */
function getChildCollectionName(field, fieldName) {
  return field?.name?.split(':').pop() || fieldName;
}

/**
 * Options for the model filter, built from the models that record events.
 * Child collections record their own events (`products:variants`), so they are
 * listed alongside their parent.
 *
 * @param {object[]} models
 * @returns {Array<{ value: string, label: string }>}
 */
export function buildEventModelOptions(models = []) {
  const options = new Map();

  for (const model of models) {
    if (!model?.name || model.events?.enabled === false) {
      continue;
    }

    const path = getEventModelPath(model);
    const label = model.plural || model.label || model.name;

    options.set(path, {
      value: path,
      label,
      app_id: model.app_id,
      custom: isCustomModel(model),
    });

    for (const [fieldName, field] of Object.entries(model.fields || {})) {
      if (field?.type !== 'collection' || field.events?.enabled === false) {
        continue;
      }

      const childName = getChildCollectionName(field, fieldName);
      const childPath = `${path}:${childName}`;

      options.set(childPath, {
        value: childPath,
        label: `${label} / ${field.plural || field.label || childName}`,
        app_id: model.app_id,
        custom: isCustomModel(model),
      });
    }
  }

  return [...options.values()].sort((a, b) => a.label.localeCompare(b.label));
}

/**
 * Find the config that records events for a model path, along with the event
 * root its types are prefixed with. Child collections carry their own events
 * config and a compound root (`product.variant`).
 *
 * @param {object[]} models
 * @param {string} modelPath
 * @returns {{ config: object, root: string } | null}
 */
function getEventModelConfig(models = [], modelPath) {
  if (!modelPath) {
    return null;
  }

  const [parentPath, childName] = modelPath.split(':');

  const model = models.find(
    (item) => item?.name && getEventModelPath(item) === parentPath,
  );

  if (!model) {
    return null;
  }

  // `events.root` is derived when the API normalizes a config rather than
  // stored on the model file, so fall back to deriving it the same way
  const root = model.events?.root || singularize(model.name);

  if (!childName) {
    return { config: model, root };
  }

  const field = Object.entries(model.fields || {}).find(
    ([fieldName, value]) =>
      value?.type === 'collection' &&
      getChildCollectionName(value, fieldName) === childName,
  );

  return field
    ? { config: field[1], root: `${root}.${singularize(childName)}` }
    : null;
}

/**
 * Every settings section shares the `settings` model and the single
 * `setting.updated` type, so the useful axis is the section itself, which the
 * event carries as `data.id`.
 *
 * @returns {Array<{ value: string, label: string }>}
 */
export function buildEventSettingsOptions() {
  return (
    Object.entries(EVENT_SETTINGS_LABELS)
      // Sections with no page in the admin (admin, geo, purchaselinks) would
      // filter to rows nobody can follow up on
      .filter(([value]) => EVENT_SETTINGS_SECTIONS[value])
      .map(([value, label]) => ({ value, label }))
      .sort((a, b) => a.label.localeCompare(b.label))
  );
}

/**
 * Event types recorded by one model, for the type filter. Values are the full
 * `root.id` string stored on the event; labels drop the root, which the model
 * filter has already established.
 *
 * @param {object[]} models
 * @param {string} modelPath
 * @returns {Array<{ value: string, label: string }>}
 */
export function buildEventTypeOptions(models, modelPath) {
  const found = getEventModelConfig(models, modelPath);
  const types = found?.config?.events?.types;

  if (!Array.isArray(types)) {
    return [];
  }

  return types.reduce((acc, type) => {
    if (type?.id) {
      acc.push({ value: `${found.root}.${type.id}`, label: type.id });
    }

    return acc;
  }, []);
}

/**
 * An app's slug for display, the same as actions/client appsById `slug_id`:
 * its public ID, else its private ID without the leading underscore. Falls
 * back to the app ID when the app isn't loaded.
 *
 * @param {object} [app] an entry of context.client.appsById
 * @param {string} [appId]
 * @returns {string}
 */
export function getAppSlug(app, appId) {
  return (
    app?.slug_id ||
    app?.public_id ||
    app?.private_id?.replace(/^_/, '') ||
    appId
  );
}

/**
 * A webhook event type for display, with app IDs in the model path
 * (`apps/<app_id>/syncs/sync.created`) and in the `$app.<app_id>.` prefix
 * replaced by the app's slug. Other events are returned as they are.
 *
 * @param {string} event
 * @param {object} [appsById] context.client.appsById
 * @returns {string}
 */
export function formatWebhookEvent(event, appsById) {
  if (typeof event !== 'string') {
    return event;
  }

  return event
    .replace(
      /^\$app\.([^./]+)\./,
      (_match, appId) => `$app.${getAppSlug(appsById?.[appId], appId)}.`,
    )
    .replace(
      /(^|\.)apps\/([^/]+)\//,
      (_match, start, appId) =>
        `${start}apps/${getAppSlug(appsById?.[appId], appId)}/`,
    );
}

/**
 * Webhook event groups for the store's custom models and for models installed
 * by apps, one group per model or child collection. Values are qualified with
 * the model path (`recipes/recipe.created`,
 * `apps/<app_id>/things/thing.updated`), a form webhook dispatch matches
 * (schema-api-server isWebhookValidForEvent) that can't collide with another
 * model using the same event root. Hook types are skipped since webhooks only
 * receive asynchronous events.
 *
 * Custom models whose event root is already in the standard list are skipped
 * with their child collections: a store's copy of a standard model can look
 * custom when its `extends` points to itself.
 *
 * @param {object[]} models
 * @param {object} [appsById] context.client.appsById, for app slugs
 * @param {string[]} [standardRoots] event roots the standard list already shows
 * @returns {{ custom: object[], apps: object[] }}
 */
export function buildWebhookModelEventOptions(
  models = [],
  appsById = {},
  standardRoots = [],
) {
  const custom = [];
  const apps = [];
  const standard = new Set(standardRoots);

  for (const model of buildEventModelOptions(models)) {
    if (!model.custom && !model.app_id) {
      continue;
    }

    if (model.custom) {
      const [parentPath] = model.value.split(':');
      const parent = getEventModelConfig(models, parentPath);

      if (standard.has(parent?.root)) {
        continue;
      }
    }

    const found = getEventModelConfig(models, model.value);
    const types = found?.config?.events?.types;

    if (!Array.isArray(types)) {
      continue;
    }

    const options = types.reduce((acc, type) => {
      if (type?.id && !type.hooks) {
        const event = `${found.root}.${type.id}`;
        acc.push({ value: `${model.value}/${event}`, label: event });
      }

      return acc;
    }, []);

    if (options.length <= 0) {
      continue;
    }

    if (model.custom) {
      custom.push({ name: model.label, path: model.value, options });
    } else {
      const appSlug = getAppSlug(appsById?.[model.app_id], model.app_id);
      apps.push({ label: `apps/${appSlug}: ${model.label}`, options });
    }
  }

  apps.sort((a, b) => a.label.localeCompare(b.label));

  // The select groups options by label, so custom models sharing a name would
  // merge into one group; those are told apart by their model path
  const nameCounts = custom.reduce((acc, { name }) => {
    acc[name] = (acc[name] || 0) + 1;
    return acc;
  }, {});

  return {
    custom: custom.map(({ name, path, options }) => ({
      label:
        nameCounts[name] > 1 ? `Custom: ${name} (${path})` : `Custom: ${name}`,
      options,
    })),
    apps,
  };
}

/**
 * Build the `where` clause for the search box. Ids have to be matched exactly,
 * and `search` cannot be combined with them: the API pushes `search` into
 * `where.$and`, so sending both would AND the two and return nothing.
 *
 * @param {string} search
 * @returns {{ where?: object, search?: string }}
 */
export function getEventSearchQuery(search) {
  const value = String(search || '').trim();

  if (!value) {
    return {};
  }

  if (isObjectId(value)) {
    return {
      where: {
        $or: [{ 'data.id': value }, { req_id: value }, { id: value }],
      },
    };
  }

  return { search: value };
}
